import React from 'react';
import { colors } from '../../styles/colors';

/**
 * BaseButton - Composant bouton unifié du design system
 *
 * Fusion de ActionButton et BaseButton pour un composant complet et cohérent.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Contenu du bouton (alternatif à text)
 * @param {string} props.text - Texte du bouton (alternatif à children)
 * @param {string} props.loadingText - Texte affiché pendant le loading
 * @param {string} props.variant - 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline'
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {boolean} props.disabled - État désactivé
 * @param {boolean} props.loading - Affiche un spinner ou texte de chargement
 * @param {Function} props.onClick - Handler de clic
 * @param {React.Component} props.icon - Composant icône Lucide (ex: Plus, RefreshCw)
 * @param {string} props.iconPosition - 'left' | 'right'
 * @param {number} props.iconSize - Taille de l'icône (défaut selon size)
 * @param {boolean} props.animateIcon - Animer l'icône en spin pendant loading
 * @param {boolean} props.fullWidth - Prend toute la largeur
 * @param {string} props.type - 'button' | 'submit' | 'reset'
 * @param {Object} props.style - Styles additionnels
 */
export default function BaseButton({
  children,
  text,
  loadingText,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  icon: Icon,
  iconPosition = 'left',
  iconSize: customIconSize,
  animateIcon = true,
  fullWidth = false,
  type = 'button',
  style = {},
  ...rest
}) {
  const [isHovered, setIsHovered] = React.useState(false);
  const isDisabled = disabled || loading;

  // Contenu du bouton (text ou children)
  const content = text || children;

  // Taille de l'icône (personnalisée ou selon size)
  const defaultIconSizes = { sm: 14, md: 16, lg: 18 };
  const iconSizeValue = customIconSize || defaultIconSizes[size];

  // Styles de base
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Icon || loading ? '0.5rem' : 0,
    fontFamily: '"Noto Sans", sans-serif',
    fontWeight: 600,
    borderRadius: '0.5rem',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'all 200ms',
    border: 'none',
    outline: 'none',
    width: fullWidth ? '100%' : 'auto',
  };

  // Tailles
  const sizeStyles = {
    sm: { padding: '0.375rem 0.75rem', fontSize: '0.75rem' },
    md: { padding: '0.625rem 1rem', fontSize: '0.875rem' },
    lg: { padding: '0.75rem 1.5rem', fontSize: '1rem' },
  };

  // Variantes - couleurs normales
  const variantStyles = {
    primary: {
      backgroundColor: colors.primary.teal,
      color: colors.text.white,
    },
    secondary: {
      backgroundColor: colors.primary.gold,
      color: colors.text.white,
    },
    danger: {
      backgroundColor: colors.status.error,
      color: colors.text.white,
    },
    ghost: {
      backgroundColor: 'transparent',
      color: colors.primary.teal,
    },
    outline: {
      backgroundColor: 'transparent',
      color: colors.primary.teal,
      border: `1px solid ${colors.primary.teal}`,
    },
  };

  // Couleurs hover
  const hoverStyles = {
    primary: { backgroundColor: colors.hover.tealDark },
    secondary: { backgroundColor: colors.hover.goldDark },
    danger: { backgroundColor: colors.hover.errorDark },
    ghost: { backgroundColor: colors.background.lightGray },
    outline: { backgroundColor: colors.background.lightBlue },
  };

  // Couleurs disabled
  const disabledStyles = {
    backgroundColor: colors.secondary.mediumGray,
    color: colors.text.white,
    border: 'none',
  };

  // Combiner les styles
  const combinedStyles = {
    ...baseStyles,
    ...sizeStyles[size],
    ...(isDisabled && !loading ? disabledStyles : variantStyles[variant]),
    ...(isHovered && !isDisabled ? hoverStyles[variant] : {}),
    ...style,
  };

  // Rendu de l'icône
  const renderIcon = () => {
    if (!Icon) return null;

    const shouldAnimate = loading && animateIcon;

    return (
      <Icon
        size={iconSizeValue}
        style={{
          animation: shouldAnimate ? 'spin 1s linear infinite' : 'none',
          flexShrink: 0,
        }}
      />
    );
  };

  // Rendu du contenu pendant loading
  const renderContent = () => {
    if (loading && loadingText) {
      return (
        <>
          {Icon && animateIcon && iconPosition === 'left' && renderIcon()}
          {loadingText}
          {Icon && animateIcon && iconPosition === 'right' && renderIcon()}
        </>
      );
    }

    if (loading && !loadingText && !Icon) {
      // Spinner générique si pas d'icône et pas de loadingText
      return (
        <span
          style={{
            width: iconSizeValue,
            height: iconSizeValue,
            border: '2px solid transparent',
            borderTopColor: 'currentColor',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      );
    }

    return (
      <>
        {Icon && iconPosition === 'left' && renderIcon()}
        {content}
        {Icon && iconPosition === 'right' && renderIcon()}
      </>
    );
  };

  return (
    <>
      <button
        type={type}
        style={combinedStyles}
        onClick={isDisabled ? undefined : onClick}
        disabled={isDisabled}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        {...rest}
      >
        {renderContent()}
      </button>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
